import type { ReactNode } from 'react';
import type { Tone } from '@walkkit/ui';
import { cn } from '../../lib/cn';
import { dismissToast, useToasts } from '../../lib/toast';
import { errorTitle } from '../../api';
import { s } from '../../strings';
import { IconAlert, IconCheck, IconX } from '../icons';
import { Button } from './button';

export const toneClass: Record<Tone, string> = {
  ok: 'bg-ok-tint text-ok',
  warn: 'bg-warn-tint text-warn',
  danger: 'bg-danger-tint text-danger',
  info: 'bg-info-tint text-info',
  neutral: 'bg-ink/[0.06] text-ink-muted',
};

export const toneDot: Record<Tone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  danger: 'bg-danger',
  info: 'bg-info',
  neutral: 'bg-ink-muted/60',
};

export function Badge({
  tone = 'neutral',
  children,
  className,
  dot,
  title,
}: {
  tone?: Tone;
  children: ReactNode;
  className?: string;
  dot?: boolean;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[12.5px] leading-5 font-medium whitespace-nowrap',
        toneClass[tone],
        className,
      )}
    >
      {dot && <span className={cn('size-1.5 rounded-full', toneDot[tone])} aria-hidden="true" />}
      {children}
    </span>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('skeleton h-4', className)} aria-hidden="true" />;
}

/** A stack of skeleton rows for list/table loading states. */
export function SkeletonRows({ rows = 5, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-3', className)} role="status" aria-label={s.common.loading}>
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="flex flex-1 flex-col gap-2">
            <Skeleton className="h-3.5 w-2/5" />
            <Skeleton className="h-3 w-1/4" />
          </div>
        </div>
      ))}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
  className,
}: {
  icon?: ReactNode;
  title: ReactNode;
  body?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('flex flex-col items-center justify-center px-6 py-12 text-center', className)}>
      {icon && (
        <div className="mb-4 flex size-12 items-center justify-center rounded-2xl bg-brand-tint text-brand" aria-hidden="true">
          {icon}
        </div>
      )}
      <h3 className="text-base font-semibold text-ink">{title}</h3>
      {body && <p className="mt-1 max-w-sm text-sm text-ink-muted">{body}</p>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry, className }: { error: unknown; onRetry?: () => void; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center px-6 py-10 text-center', className)} role="alert">
      <div className="mb-3 flex size-11 items-center justify-center rounded-2xl bg-danger-tint text-danger" aria-hidden="true">
        <IconAlert />
      </div>
      <h3 className="font-semibold text-ink">{s.common.errorTitle}</h3>
      <p className="mt-1 text-sm text-ink-muted">{errorTitle(error, s.common.errorBody)}</p>
      {onRetry && (
        <Button className="mt-4" size="sm" onClick={onRetry}>
          {s.common.retry}
        </Button>
      )}
    </div>
  );
}

export function Banner({
  tone = 'info',
  title,
  children,
  action,
  icon,
  className,
}: {
  tone?: Tone;
  title: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-card border p-4 sm:flex-row sm:items-center',
        tone === 'danger' && 'border-danger/20 bg-danger-tint/60',
        tone === 'warn' && 'border-warn/20 bg-warn-tint/70',
        tone === 'info' && 'border-info/15 bg-info-tint/60',
        tone === 'ok' && 'border-ok/20 bg-ok-tint/60',
        tone === 'neutral' && 'border-line bg-surface',
        className,
      )}
    >
      {icon && <div className={cn('shrink-0', toneClass[tone].split(' ')[1])}>{icon}</div>}
      <div className="flex-1">
        <p className="font-semibold text-ink">{title}</p>
        {children && <div className="mt-0.5 text-sm text-ink-muted">{children}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  );
}

export function Toaster() {
  const toasts = useToasts();
  return (
    <div
      className="pointer-events-none fixed inset-x-0 bottom-0 z-[60] flex flex-col items-center gap-2 p-4 sm:items-end"
      role="status"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <div
          key={t.id}
          className={cn(
            'pointer-events-auto flex w-full max-w-sm items-start gap-3 rounded-xl border bg-ink px-4 py-3 text-white shadow-pop',
            t.tone === 'danger' ? 'border-danger/40' : 'border-white/10',
          )}
        >
          <span
            className={cn(
              'mt-0.5 flex size-5 shrink-0 items-center justify-center rounded-full',
              t.tone === 'danger' ? 'bg-danger' : t.tone === 'ok' ? 'bg-ok' : 'bg-info',
            )}
            aria-hidden="true"
          >
            {t.tone === 'danger' ? <IconAlert size={12} /> : <IconCheck size={12} />}
          </span>
          <div className="flex-1 text-sm">
            <p className="font-medium">{t.title}</p>
            {t.detail && <p className="mt-0.5 text-white/70">{t.detail}</p>}
          </div>
          <button
            type="button"
            onClick={() => dismissToast(t.id)}
            className="-m-2 flex size-9 items-center justify-center rounded-lg text-white/60 hover:bg-white/10 hover:text-white"
            aria-label={s.common.close}
          >
            <IconX size={16} />
          </button>
        </div>
      ))}
    </div>
  );
}
